"use client"

import { useState } from "react"
import { Send } from "lucide-react"
import { cn } from "@/lib/utils"
import type { I18n, InputType, Language } from "@/lib/chatbot/types"
import { BOTON, CAMPO } from "./estilos"

interface InputFieldProps {
  inputType: InputType
  placeholder?: I18n
  language: Language
  disabled?: boolean
  onSubmit: (value: string) => void
}

const COPY = {
  invalidRef: { es: "La referencia debe empezar por 'P'", en: "Reference must start with 'P'" },
  invalidNumber: { es: "Introduce un número válido", en: "Enter a valid number" },
  invalidEmail: { es: "Introduce un email válido", en: "Enter a valid email" },
  empty: { es: "Este campo no puede estar vacío", en: "This field can't be empty" },
}

export function InputField({ inputType, placeholder, language, disabled, onSubmit }: InputFieldProps) {
  const [value, setValue] = useState("")
  const [error, setError] = useState<string | null>(null)

  function validate(v: string): string | null {
    const trimmed = v.trim()
    if (!trimmed) return COPY.empty[language]
    if (inputType === "ref" && !/^P/i.test(trimmed)) return COPY.invalidRef[language]
    if (inputType === "number" && !/^\d+$/.test(trimmed)) return COPY.invalidNumber[language]
    if (inputType === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return COPY.invalidEmail[language]
    return null
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (disabled) return
    const err = validate(value)
    if (err) {
      setError(err)
      return
    }
    setError(null)
    // Catalog references are uppercase (P1234); normalize so the CRM lookup
    // can't miss because the visitor typed 'p1234'.
    const trimmed = value.trim()
    onSubmit(inputType === "ref" ? trimmed.toUpperCase() : trimmed)
    setValue("")
  }

  const htmlType = inputType === "number" ? "tel" : inputType === "email" ? "email" : "text"

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-center gap-2">
        <input
          type={htmlType}
          inputMode={inputType === "number" ? "numeric" : undefined}
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={placeholder?.[language]}
          disabled={disabled}
          className={cn(CAMPO, "min-w-0 flex-1")}
          autoFocus
        />
        <button
          type="submit"
          disabled={disabled || !value.trim()}
          aria-label="Enviar"
          className={cn(BOTON, "w-9 shrink-0")}
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </form>
  )
}
